/**
 * Browser env resolution — window.__ENV__ (deploy) first, then Vite define, then process/dev.
 */

import {
  getEnvValue,
  getOptionalEnvValue,
  isProductionRuntime,
} from '@template-project/storefront-shared';
import { DEV_ENV_DEFAULTS, type EnvConfig } from './env.types';

declare const __CHECKOUT_URL__: string | undefined;
declare const __CHECKOUT_MIDDLEWARE_URL__: string | undefined;
declare const __PRODUCTS_API_URL__: string | undefined;
declare const __CARTS_API_URL__: string | undefined;
declare const __CAMPAIGNS_API_URL__: string | undefined;
declare const __CAMPAIGNS_TENANT_ID__: string | undefined;
declare const __FUNNELS_API_URL__: string | undefined;
declare const __FUNNELS_TENANT_ID__: string | undefined;
declare const __FUNNELS_STORE_ID__: string | undefined;
declare const __GTM_ID__: string | undefined;
declare const __PUBLIC_PRODUCT_FEED_URL__: string | undefined;
declare const __BASE_URL__: string | undefined;
declare const __STOREFRONT_BFF_URL__: string | undefined;
declare const __STOREFRONT_AUTH_BFF_URL__: string | undefined;
declare const __STOREFRONT_BFF_USE_DEV_PROXY__: string | undefined;
declare const __RUNTIME_VERSION__: string | undefined;
declare const __LOGIN_IMAGE_URL__: string | undefined;
declare const __CDN_HOSTS__: string | undefined;
declare const __CDN_BASE_URL__: string | undefined;
declare const __DEV__: boolean | undefined;

function dropLoopbackUrlInProduction(value: string): string {
  const production = isProductionRuntime(typeof __DEV__ !== 'undefined' ? __DEV__ : undefined);
  if (!production) return value;
  return /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/.test(value) ? '' : value;
}

function sanitizeHttpsImageUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed);
    if (parsed.protocol !== 'https:') return '';
    return trimmed;
  } catch {
    return '';
  }
}

function isNonEmpty(value: string | undefined): value is string {
  return typeof value === 'string' && value.length > 0;
}

function useDevBffProxy(): boolean {
  const viteDev = !isProductionRuntime(typeof __DEV__ !== 'undefined' ? __DEV__ : undefined);
  return viteDev && __STOREFRONT_BFF_USE_DEV_PROXY__ === 'true';
}

/** In Vite dev, `.env.local` wins over `public/env-config.js` loopback defaults. */
function resolveStorefrontBffUrl(
  viteGlobal: string | undefined,
  windowEnvKey: string,
  devDefault: string,
): string {
  const viteDev = !isProductionRuntime(typeof __DEV__ !== 'undefined' ? __DEV__ : undefined);
  if (viteDev && isNonEmpty(viteGlobal)) {
    return viteGlobal;
  }
  return getOptionalEnvValue(windowEnvKey, viteGlobal, windowEnvKey, devDefault);
}

/**
 * Build browser env. When this module is imported during SSR (side-effect of the
 * facade), skip getEnvValue so we don't spam missing-config logs — SSR uses env.node.
 */
function buildClientEnv(): EnvConfig {
  if (typeof window === 'undefined') {
    return { ...DEV_ENV_DEFAULTS };
  }

  return {
    PRODUCTS_API_URL: getEnvValue({
      windowEnvKey: 'VITE_PRODUCTS_API_URL',
      viteGlobal: typeof __PRODUCTS_API_URL__ !== 'undefined' ? __PRODUCTS_API_URL__ : undefined,
      processEnvKey: 'VITE_PRODUCTS_API_URL',
      devDefault: DEV_ENV_DEFAULTS.PRODUCTS_API_URL,
      runtimeEnvMode: 'production-only',
      devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
    }),
    CARTS_API_URL: getEnvValue({
      windowEnvKey: 'VITE_CARTS_API_URL',
      viteGlobal: typeof __CARTS_API_URL__ !== 'undefined' ? __CARTS_API_URL__ : undefined,
      processEnvKey: 'VITE_CARTS_API_URL',
      devDefault: DEV_ENV_DEFAULTS.CARTS_API_URL,
      runtimeEnvMode: 'production-only',
      devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
    }),
    CHECKOUT_URL: getEnvValue({
      windowEnvKey: 'VITE_CHECKOUT_URL',
      viteGlobal: typeof __CHECKOUT_URL__ !== 'undefined' ? __CHECKOUT_URL__ : undefined,
      processEnvKey: 'VITE_CHECKOUT_URL',
      devDefault: DEV_ENV_DEFAULTS.CHECKOUT_URL,
      runtimeEnvMode: 'always',
      devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
    }),
    CHECKOUT_MIDDLEWARE_URL: getEnvValue({
      windowEnvKey: 'VITE_CHECKOUT_MIDDLEWARE_URL',
      viteGlobal:
        typeof __CHECKOUT_MIDDLEWARE_URL__ !== 'undefined' ? __CHECKOUT_MIDDLEWARE_URL__ : undefined,
      processEnvKey: 'VITE_CHECKOUT_MIDDLEWARE_URL',
      devDefault: DEV_ENV_DEFAULTS.CHECKOUT_MIDDLEWARE_URL,
      runtimeEnvMode: 'always',
      devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
    }),
    CAMPAIGNS_API_URL: getEnvValue({
      windowEnvKey: 'VITE_CAMPAIGNS_API_URL',
      viteGlobal: typeof __CAMPAIGNS_API_URL__ !== 'undefined' ? __CAMPAIGNS_API_URL__ : undefined,
      processEnvKey: 'VITE_CAMPAIGNS_API_URL',
      devDefault: DEV_ENV_DEFAULTS.CAMPAIGNS_API_URL,
      runtimeEnvMode: 'production-only',
      devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
    }),
    CAMPAIGNS_TENANT_ID: getEnvValue({
      windowEnvKey: 'VITE_CAMPAIGNS_TENANT_ID',
      viteGlobal: typeof __CAMPAIGNS_TENANT_ID__ !== 'undefined' ? __CAMPAIGNS_TENANT_ID__ : undefined,
      processEnvKey: 'VITE_CAMPAIGNS_TENANT_ID',
      devDefault: DEV_ENV_DEFAULTS.CAMPAIGNS_TENANT_ID,
      runtimeEnvMode: 'production-only',
      devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
    }),
    FUNNELS_API_URL: getEnvValue({
      windowEnvKey: 'VITE_FUNNELS_API_URL',
      viteGlobal: typeof __FUNNELS_API_URL__ !== 'undefined' ? __FUNNELS_API_URL__ : undefined,
      processEnvKey: 'VITE_FUNNELS_API_URL',
      devDefault: DEV_ENV_DEFAULTS.FUNNELS_API_URL,
      runtimeEnvMode: 'always',
      devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
    }),
    FUNNELS_TENANT_ID: getEnvValue({
      windowEnvKey: 'VITE_FUNNELS_TENANT_ID',
      viteGlobal: typeof __FUNNELS_TENANT_ID__ !== 'undefined' ? __FUNNELS_TENANT_ID__ : undefined,
      processEnvKey: 'VITE_FUNNELS_TENANT_ID',
      devDefault: DEV_ENV_DEFAULTS.FUNNELS_TENANT_ID,
      // 'always' so window.__ENV__ / env-config(.local).js can override funnel tenant in dev.
      runtimeEnvMode: 'always',
      devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
    }),
    FUNNELS_STORE_ID: getOptionalEnvValue(
      'VITE_FUNNELS_STORE_ID',
      typeof __FUNNELS_STORE_ID__ !== 'undefined' ? __FUNNELS_STORE_ID__ : undefined,
      'VITE_FUNNELS_STORE_ID',
      DEV_ENV_DEFAULTS.FUNNELS_STORE_ID,
    ),
    GTM_ID: getOptionalEnvValue(
      'VITE_GTM_ID',
      typeof __GTM_ID__ !== 'undefined' ? __GTM_ID__ : undefined,
      'VITE_GTM_ID',
      DEV_ENV_DEFAULTS.GTM_ID,
    ),
    PUBLIC_PRODUCT_FEED_URL: getOptionalEnvValue(
      'VITE_PUBLIC_PRODUCT_FEED_URL',
      typeof __PUBLIC_PRODUCT_FEED_URL__ !== 'undefined' ? __PUBLIC_PRODUCT_FEED_URL__ : undefined,
      'VITE_PUBLIC_PRODUCT_FEED_URL',
      DEV_ENV_DEFAULTS.PUBLIC_PRODUCT_FEED_URL,
    ),
    BASE_URL: getEnvValue({
      windowEnvKey: 'VITE_BASE_URL',
      viteGlobal: typeof __BASE_URL__ !== 'undefined' ? __BASE_URL__ : undefined,
      processEnvKey: 'VITE_BASE_URL',
      devDefault: DEV_ENV_DEFAULTS.BASE_URL,
      runtimeEnvMode: 'production-only',
      devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
    }),
    STOREFRONT_BFF_URL: useDevBffProxy()
      ? ''
      : dropLoopbackUrlInProduction(
          resolveStorefrontBffUrl(
            typeof __STOREFRONT_BFF_URL__ !== 'undefined' ? __STOREFRONT_BFF_URL__ : undefined,
            'VITE_STOREFRONT_BFF_URL',
            DEV_ENV_DEFAULTS.STOREFRONT_BFF_URL,
          ),
        ),
    STOREFRONT_AUTH_BFF_URL: useDevBffProxy()
      ? ''
      : dropLoopbackUrlInProduction(
          resolveStorefrontBffUrl(
            typeof __STOREFRONT_AUTH_BFF_URL__ !== 'undefined' ? __STOREFRONT_AUTH_BFF_URL__ : undefined,
            'VITE_STOREFRONT_AUTH_BFF_URL',
            DEV_ENV_DEFAULTS.STOREFRONT_AUTH_BFF_URL,
          ) ||
            dropLoopbackUrlInProduction(
              resolveStorefrontBffUrl(
                typeof __STOREFRONT_BFF_URL__ !== 'undefined' ? __STOREFRONT_BFF_URL__ : undefined,
                'VITE_STOREFRONT_BFF_URL',
                DEV_ENV_DEFAULTS.STOREFRONT_BFF_URL,
              ),
            ),
        ),
    RUNTIME_VERSION: getEnvValue({
      windowEnvKey: 'VITE_RUNTIME_VERSION',
      viteGlobal: typeof __RUNTIME_VERSION__ !== 'undefined' ? __RUNTIME_VERSION__ : undefined,
      processEnvKey: 'VITE_RUNTIME_VERSION',
      devDefault: DEV_ENV_DEFAULTS.RUNTIME_VERSION,
      runtimeEnvMode: 'production-only',
      devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
    }),
    LOGIN_IMAGE_URL: sanitizeHttpsImageUrl(
      getOptionalEnvValue(
        'VITE_LOGIN_IMAGE_URL',
        typeof __LOGIN_IMAGE_URL__ !== 'undefined' ? __LOGIN_IMAGE_URL__ : undefined,
        'VITE_LOGIN_IMAGE_URL',
        DEV_ENV_DEFAULTS.LOGIN_IMAGE_URL,
      ),
    ),
    CDN_HOSTS: getOptionalEnvValue(
      'VITE_CDN_HOSTS',
      typeof __CDN_HOSTS__ !== 'undefined' ? __CDN_HOSTS__ : undefined,
      'VITE_CDN_HOSTS',
      DEV_ENV_DEFAULTS.CDN_HOSTS,
    ),
    CDN_BASE_URL: getEnvValue({
      windowEnvKey: 'VITE_CDN_BASE_URL',
      viteGlobal: typeof __CDN_BASE_URL__ !== 'undefined' ? __CDN_BASE_URL__ : undefined,
      processEnvKey: 'VITE_CDN_BASE_URL',
      devDefault: DEV_ENV_DEFAULTS.CDN_BASE_URL,
      runtimeEnvMode: 'always',
      devFlag: typeof __DEV__ !== 'undefined' ? __DEV__ : undefined,
    }),
  };
}

export const env: EnvConfig = buildClientEnv();

export const isDev = !isProductionRuntime(typeof __DEV__ !== 'undefined' ? __DEV__ : undefined);

export function getApiUrl(apiName: keyof EnvConfig, proxyPath?: string): string {
  if (isDev && proxyPath) return proxyPath;
  return env[apiName];
}
